import { api } from '../api';
import { validate as isUUID } from 'uuid';

export interface Visitor {
  id: string;
  name: string;
  document_number: string;
  phone_number: string;
  status_flag: 'ACTIVE' | 'INACTIVE';
  start_date: string;
  end_date: string;
  tenant: {
    id: string;
    name: string;
  };
  user: {
    id: string;
    name: string;
  };
  areas: Array<{
    id: string;
    name: string;
  }>;
  images?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateVisitorData {
  name: string;
  document: string;
  phone_number: string;
  status_flag: 'ACTIVE' | 'INACTIVE';
  start_date: string;
  end_date: string;
  tenant_id: string;
  user_id: string;
  area_id: string | string[];
  file?: string; // base64 string única
}

export interface UpdateVisitorData extends Partial<CreateVisitorData> {}

interface ListVisitorsParams {
  page?: number;
  limit?: number;
  search?: string;
}

const validateAreaIds = (areaIds: string | string[]): boolean => {
  if (Array.isArray(areaIds)) {
    return areaIds.every(id => isUUID(id));
  }
  return isUUID(areaIds);
};

export const visitorService = {
  list: async (params?: ListVisitorsParams) => {
    const response = await api.get('/visitors', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/visitors/${id}`);
    return response.data;
  },

  create: async (data: CreateVisitorData) => {
    // Validar area_id antes de criar
    if (data.area_id && !validateAreaIds(data.area_id)) {
      throw new Error('IDs de área inválidos fornecidos');
    }

    const requestData = {
      name: data.name,
      document: data.document,
      phone_number: data.phone_number,
      status_flag: data.status_flag,
      start_date: data.start_date,
      end_date: data.end_date,
      user_id: data.user_id,
      area_id: Array.isArray(data.area_id) ? data.area_id : [data.area_id],
      file: data.file || ''
    };

    const response = await api.post('/visitors', requestData);
    return response.data;
  },

  update: async (id: string, data: UpdateVisitorData) => {
    // Validar area_id antes de atualizar
    if (data.area_id && !validateAreaIds(data.area_id)) {
      throw new Error('IDs de área inválidos fornecidos');
    }

    const requestData: any = {};
    
    // Adicionar apenas campos que foram fornecidos
    if (data.name) requestData.name = data.name;
    if (data.document) requestData.document = data.document;
    if (data.phone_number) requestData.phone_number = data.phone_number;
    if (data.status_flag) requestData.status_flag = data.status_flag;
    if (data.start_date) requestData.start_date = data.start_date;
    if (data.end_date) requestData.end_date = data.end_date;
    if (data.user_id) requestData.user_id = data.user_id;
    
    // Tratar area_id
    if (data.area_id) {
      requestData.area_id = Array.isArray(data.area_id) ? data.area_id : [data.area_id];
    }
    
    // Adicionar imagens se houver
    if (data.file) {
      requestData.file = data.file;
    }

    const response = await api.post(`/visitors/${id}`, requestData);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/visitors/${id}`);
    return response.data;
  },
};